/**
 * DarkBarChart — Dimus Video Components
 *
 * Barras verticais narrow com valores DENTRO (padrão aprovado 30/05/2026).
 * Suporta comparação 2-N barras, label externo, badge contextual.
 *
 * Família: Dark Cinematic / Glass (ambas)
 * Design ref: v9-cafe-preco-minimo.html (padrão de referência aprovado)
 * ADR: ia-skills ADR-0001 (componente canônico, não editar no servidor)
 *
 * Props-first: todos os valores editáveis como props tipadas.
 * Animated: height via interpolate(expoOut), entrada staggerada por barra.
 */
import React from "react";
import { interpolate, spring, useCurrentFrame } from "remotion";
import { Easing } from "remotion";

// ── Easings ─────────────────────────────────────────────────────────────────
const ease = {
  expoOut: Easing.bezier(0.19, 1, 0.22, 1),
  power3Out: Easing.bezier(0.215, 0.61, 0.355, 1),
  backOut: Easing.bezier(0.34, 1.56, 0.64, 1),
};

// ── Types ────────────────────────────────────────────────────────────────────
export interface BarDatum {
  /** Texto exibido DENTRO da barra (ex: "R$3k") */
  value: string;
  /** Label externo abaixo da barra (ex: "O QUE COBRA") */
  label: string;
  /** Altura em px da barra (valor relativo — eixo Y implícito) */
  heightPx: number;
  /** Cor da barra (hex ou rgba). Default: vermelho Dimus */
  color?: string;
  /** Cor de glow do boxShadow. Default: mesma que color com 30% alpha */
  glowColor?: string;
}

export interface SeparatorConfig {
  /** Símbolo entre barras (ex: "≠", "vs", "→"). Default: "≠" */
  symbol: string;
  /** Cor do símbolo. Default: rgba(255,255,255,0.12) */
  color?: string;
  /** Tamanho em px. Default: 90 */
  fontSize?: number;
}

export interface DarkBarChartProps {
  /** Array de barras (mínimo 1, máximo 4 para legibilidade 9:16) */
  bars: BarDatum[];
  /** Separador entre barras. Null = sem separador */
  separator?: SeparatorConfig | null;
  /** Frame de início da animação de entrada */
  startFrame?: number;
  /** Duração de cada barra em frames. Default: 28 */
  animDuration?: number;
  /** Stagger entre barras em frames. Default: 12 */
  stagger?: number;
  /** Cor do texto DENTRO da barra. Default: #fff */
  valueColor?: string;
  /** Tamanho do texto interno. Default: 68 */
  valueFontSize?: number;
  /** Fonte do texto interno. Default: "Anton, Impact, sans-serif" */
  valueFont?: string;
  /** Cor dos labels externos. Default: #555 */
  labelColor?: string;
  /** Tamanho dos labels externos. Default: 24 */
  labelFontSize?: number;
  /** Largura de cada barra em px. Default: 200 */
  barWidth?: number;
  /** Gap horizontal entre barras em px. Default: 60 */
  barGap?: number;
  /** Border-radius topo das barras. Default: 16 */
  barRadius?: number;
}

// ── Defaults ────────────────────────────────────────────────────────────────
const DEFAULTS = {
  startFrame: 0,
  animDuration: 28,
  stagger: 12,
  valueColor: "#fff",
  valueFontSize: 68,
  valueFont: "'Anton', Impact, sans-serif",
  labelColor: "#555",
  labelFontSize: 24,
  barWidth: 200,
  barGap: 60,
  barRadius: 16,
};

// ── Single Bar ───────────────────────────────────────────────────────────────
const Bar: React.FC<{
  datum: BarDatum;
  barWidth: number;
  animDuration: number;
  localStartFrame: number;
  valueColor: string;
  valueFontSize: number;
  valueFont: string;
  labelColor: string;
  labelFontSize: number;
  barRadius: number;
}> = ({
  datum,
  barWidth,
  animDuration,
  localStartFrame,
  valueColor,
  valueFontSize,
  valueFont,
  labelColor,
  labelFontSize,
  barRadius,
}) => {
  const frame = useCurrentFrame();
  const local = Math.max(0, frame - localStartFrame);

  const barColor = datum.color ?? "#FF5050";
  const glow = datum.glowColor ?? `${barColor}4D`;

  // Height animates from 0 → heightPx with expoOut
  const height = interpolate(local, [0, animDuration], [0, datum.heightPx], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: ease.expoOut,
  });

  // Value text pops in with spring after bar is 50% grown
  const textDelay = Math.floor(animDuration * 0.5);
  const textSpring = spring({
    frame: Math.max(0, local - textDelay),
    fps: 30,
    config: { damping: 12, stiffness: 130 },
    from: 0,
    to: 1,
  });
  const textOp = interpolate(textSpring, [0, 0.4], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
      }}
    >
      {/* Bar */}
      <div
        style={{
          width: barWidth,
          height,
          background: `linear-gradient(180deg, ${barColor} 0%, ${adjustBrightness(barColor, -40)} 100%)`,
          borderRadius: `${barRadius}px ${barRadius}px 0 0`,
          boxShadow: `0 0 60px ${glow}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          willChange: "height",
          overflow: "hidden",
        }}
      >
        {/* Value inside bar */}
        <span
          style={{
            fontFamily: valueFont,
            fontSize: valueFontSize,
            color: valueColor,
            textShadow: "0 3px 16px rgba(0,0,0,.6)",
            opacity: textOp,
            transform: `scale(${interpolate(textSpring, [0, 1], [0.7, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            })})`,
            display: "inline-block",
            whiteSpace: "nowrap",
          }}
        >
          {datum.value}
        </span>
      </div>

      {/* Divider line */}
      <div
        style={{
          height: 3,
          width: barWidth,
          background: `${barColor}4D`,
        }}
      />

      {/* External label */}
      <div
        style={{
          marginTop: 18,
          fontWeight: 800,
          fontSize: labelFontSize,
          color: labelColor,
          textTransform: "uppercase" as const,
          textAlign: "center" as const,
          lineHeight: 1.3,
          fontFamily: "system-ui, -apple-system, sans-serif",
          opacity: textOp,
          maxWidth: barWidth + 20,
        }}
      >
        {datum.label}
      </div>
    </div>
  );
};

// ── Helper: darken hex color ─────────────────────────────────────────────────
function adjustBrightness(hex: string, amount: number): string {
  if (!hex.startsWith("#")) return hex;
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

// ── Main Component ───────────────────────────────────────────────────────────
export const DarkBarChart: React.FC<DarkBarChartProps> = ({
  bars,
  separator = { symbol: "≠" },
  startFrame = DEFAULTS.startFrame,
  animDuration = DEFAULTS.animDuration,
  stagger = DEFAULTS.stagger,
  valueColor = DEFAULTS.valueColor,
  valueFontSize = DEFAULTS.valueFontSize,
  valueFont = DEFAULTS.valueFont,
  labelColor = DEFAULTS.labelColor,
  labelFontSize = DEFAULTS.labelFontSize,
  barWidth = DEFAULTS.barWidth,
  barGap = DEFAULTS.barGap,
  barRadius = DEFAULTS.barRadius,
}) => {
  const frame = useCurrentFrame();

  const sep = separator ?? { symbol: "≠" };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        gap: barGap,
        width: "100%",
      }}
    >
      {bars.map((bar, i) => (
        <React.Fragment key={i}>
          <Bar
            datum={bar}
            barWidth={barWidth}
            animDuration={animDuration}
            localStartFrame={startFrame + i * stagger}
            valueColor={valueColor}
            valueFontSize={valueFontSize}
            valueFont={valueFont}
            labelColor={labelColor}
            labelFontSize={labelFontSize}
            barRadius={barRadius}
          />

          {/* Separator between bars (not after last) */}
          {separator !== null && i < bars.length - 1 && (() => {
            const sepDelay = startFrame + i * stagger;
            const sepLocal = Math.max(0, frame - sepDelay);
            const sepOp = interpolate(sepLocal, [animDuration, animDuration + 18], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                style={{
                  fontFamily: "'Anton', Impact, sans-serif",
                  fontSize: sep.fontSize ?? 90,
                  color: sep.color ?? "rgba(255,255,255,0.12)",
                  paddingBottom: 90,
                  opacity: sepOp,
                }}
              >
                {sep.symbol}
              </div>
            );
          })()}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Usage Example (for reference — never used in production) ─────────────────
/*
<DarkBarChart
  startFrame={BEAT.dado}
  bars={[
    { value: "R$3k", label: "O QUE\nCOBRA", heightPx: 300, color: "#FF5050" },
    { value: "R$5k", label: "PISO\nREAL",   heightPx: 450, color: "#1FE38A" },
  ]}
  separator={{ symbol: "≠" }}
/>
*/

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
